import { db } from './firebase'
import { collection, addDoc, serverTimestamp, query, getDocs, limit } from 'firebase/firestore'
import { 
  minimizeAnalyticsData, 
  validateAnalyticsEvent, 
  analyticsRateLimiter,
  createAuditLog 
} from '../utils/security'

// Analytics Event Types
export type AnalyticsEvent = 
  | 'session_start'
  | 'session_end'
  | 'audio_play_start'
  | 'audio_play_pause'
  | 'audio_milestone_3min'
  | 'audio_milestone_5min'
  | 'survey_displayed'
  | 'survey_completed'
  | 'survey_skipped'
  | 'completion_reached'
  | 'performance_metric'
  | 'error_occurred'

export interface AnalyticsEventData {
  eventType: AnalyticsEvent
  sessionId: string
  timestamp: any  // Firebase Timestamp
  
  // Session metadata (anonymous)
  deviceType?: 'desktop' | 'tablet' | 'mobile'
  browserInfo?: string
  screenResolution?: string
  
  // Audio playback data
  playbackDuration?: number // in seconds
  audioPosition?: number    // current position when event occurred
  
  // Survey data
  surveyRating?: 'excellent' | 'good' | 'fair' | 'poor'
  
  // Error data
  errorType?: 'network' | 'device' | 'hls' | 'session' | 'timeout' | 'general'
  errorMessage?: string
  
  // Performance data
  ttfp?: number // Time to First Play in milliseconds
  performanceMetricName?: string // Name of the performance metric (FCP, LCP, etc.)
  performanceMetricValue?: number // Value in milliseconds
  performanceRating?: 'good' | 'needs-improvement' | 'poor'
  
  // Additional context (all anonymous)
  userAgent?: string
  referrer?: string
  language?: string
}

export class AnalyticsService {
  private static instance: AnalyticsService
  private sessionId: string
  private sessionStartTime: number
  private loggedEvents: Set<string> = new Set() // Track already logged events
  
  private constructor() {
    this.sessionId = this.getOrCreateSessionId()
    this.sessionStartTime = Date.now()
    this.loadLoggedEvents()
  }
  
  public static getInstance(): AnalyticsService {
    if (!AnalyticsService.instance) {
      AnalyticsService.instance = new AnalyticsService()
    }
    return AnalyticsService.instance
  }
  
  private generateSessionId(): string {
    // Generate anonymous session ID (UUID v4)
    return 'xxxx-xxxx-4xxx-yxxx'.replace(/[xy]/g, function(c) {
      const r = Math.random() * 16 | 0
      const v = c === 'x' ? r : (r & 0x3 | 0x8)
      return v.toString(16)
    }) + '-' + Date.now()
  }
  
  private getOrCreateSessionId(): string {
    // Try to get existing session ID from sessionStorage first
    const existingSessionId = sessionStorage.getItem('hep5_session_id')
    if (existingSessionId) {
      console.log('🔄 Reusing existing session ID:', existingSessionId)
      return existingSessionId
    }
    
    // Generate new session ID and store it
    const newSessionId = this.generateSessionId()
    sessionStorage.setItem('hep5_session_id', newSessionId)
    console.log('🆕 Created new session ID:', newSessionId)
    return newSessionId
  }
  
  private loadLoggedEvents(): void {
    try {
      const savedEvents = sessionStorage.getItem('hep5_logged_events')
      if (savedEvents) {
        const eventArray = JSON.parse(savedEvents)
        this.loggedEvents = new Set(eventArray)
        console.log('📋 Loaded logged events:', eventArray)
      }
    } catch (error) {
      console.warn('Failed to load logged events:', error)
      this.loggedEvents = new Set()
    }
  }
  
  private saveLoggedEvents(): void {
    try {
      const eventArray = Array.from(this.loggedEvents)
      sessionStorage.setItem('hep5_logged_events', JSON.stringify(eventArray))
    } catch (error) {
      console.warn('Failed to save logged events:', error)
    }
  }
  
  private getDeviceInfo() {
    const ua = navigator.userAgent
    let deviceType: 'desktop' | 'tablet' | 'mobile' = 'desktop'
    
    if (/tablet|ipad|playbook|silk/i.test(ua)) {
      deviceType = 'tablet'
    } else if (/mobile|iphone|ipod|android|blackberry|opera|mini|windows\sce|palm|smartphone|iemobile/i.test(ua)) {
      deviceType = 'mobile'
    }
    
    return {
      deviceType,
      browserInfo: this.getBrowserInfo(),
      screenResolution: `${screen.width}x${screen.height}`,
      userAgent: ua.substring(0, 200), // Truncate for privacy
      language: navigator.language
    }
  }
  
  private getBrowserInfo(): string {
    const ua = navigator.userAgent
    if (ua.includes('Chrome')) return 'Chrome'
    if (ua.includes('Firefox')) return 'Firefox'
    if (ua.includes('Safari')) return 'Safari'
    if (ua.includes('Edge')) return 'Edge'
    return 'Other'
  }
  
  public async trackEvent(eventType: AnalyticsEvent, additionalData?: Partial<AnalyticsEventData>) {
    try {
      console.log(`🎯 Tracking event: ${eventType}`, additionalData)
      console.log(`🔍 [DEBUG] Database connection status:`, !!db)
      console.log(`🔍 [DEBUG] Current hostname:`, window.location.hostname)
      console.log(`🔍 [DEBUG] User agent:`, navigator.userAgent.substring(0, 50))

      // Only track essential events to reduce database load
      const essentialEvents = ['session_start', 'audio_play_start', 'audio_milestone_3min', 'completion_reached', 'survey_completed', 'survey_skipped'];
      const isEssentialEvent = essentialEvents.includes(eventType);

      // Skip non-essential events entirely
      if (!isEssentialEvent) {
        console.log(`🚫 [OPTIMIZATION] Skipping non-essential event: ${eventType}`);
        return;
      }

      // Apply rate limiting only to non-critical events
      const criticalEvents = ['survey_completed', 'completion_reached', 'session_start'];
      const isCriticalEvent = criticalEvents.includes(eventType);
      
      if (!isCriticalEvent) {
        if (!analyticsRateLimiter.isAllowed(this.sessionId)) {
          console.warn('Analytics event rate limited for session:', this.sessionId)
          return
        }
      } else {
        console.log(`🔵 [CRITICAL EVENT] ${eventType} bypassed rate limiting`);
      }
      
      const deviceInfo = this.getDeviceInfo()
      
      let eventData: AnalyticsEventData = {
        eventType,
        sessionId: this.sessionId,
        timestamp: serverTimestamp(),
        ...deviceInfo,
        ...additionalData
      }
      
      // Data validation
      const validation = validateAnalyticsEvent(eventData)
      if (!validation.valid) {
        console.warn('Invalid analytics event data:', validation.errors)
        createAuditLog('analytics_validation_failed', { eventType, errors: validation.errors })
        return
      }
      
      // Pre-minimization debugging removed for performance
      
      // Data minimization and sanitization
      eventData = minimizeAnalyticsData(eventData)
      
      // Post-minimization debugging removed for performance
      
      // Store in Firestore (if available) with mobile device specific handling
      if (db) {
        console.log('💾 Storing event in Firestore:', eventType)

        // Enhanced mobile device detection for context only
        // const isMobile = /mobile|iphone|ipod|android|blackberry|opera|mini|windows\sce|palm|smartphone|iemobile/i.test(navigator.userAgent)
        // const isTablet = /tablet|ipad|playbook|silk/i.test(navigator.userAgent)
        
        const isIPhoneSafari = /iPhone.*Safari/.test(navigator.userAgent)
        let docRef

        if (isIPhoneSafari && (eventType === 'survey_completed' || eventType === 'survey_skipped')) {
          console.log('🍎 [SAFARI DEBUG] Using retry mechanism for critical survey event')

          // Retry mechanism for iPhone Safari survey events
          let attempts = 0
          const maxAttempts = 3

          while (attempts < maxAttempts) {
            try {
              attempts++
              console.log(`🍎 [SAFARI DEBUG] Attempt ${attempts}/${maxAttempts} to store survey event`)

              docRef = await Promise.race([
                addDoc(collection(db, 'analytics_events'), eventData),
                new Promise<never>((_, reject) => {
                  setTimeout(() => reject(new Error(`Firestore timeout on attempt ${attempts}`)), 3000)
                })
              ])

              console.log(`🍎 [SAFARI DEBUG] Survey event stored successfully on attempt ${attempts}, ID:`, docRef.id)
              break

            } catch (error) {
              console.error(`🍎 [SAFARI DEBUG] Attempt ${attempts} failed:`, error)

              if (attempts >= maxAttempts) {
                console.error('🍎 [SAFARI DEBUG] All attempts failed, storing in fallback')

                // Store in localStorage as absolute fallback
                const fallbackKey = `hep5_fallback_${eventType}_${Date.now()}`
                localStorage.setItem(fallbackKey, JSON.stringify({
                  eventData,
                  attempts,
                  lastError: error instanceof Error ? error.message : String(error),
                  timestamp: new Date().toISOString()
                }))

                throw error
              }

              // Wait before retry (exponential backoff)
              await new Promise(resolve => setTimeout(resolve, attempts * 1000))
            }
          }
        } else {
          // Normal single attempt for non-critical events or other browsers
          try {
            docRef = await addDoc(collection(db, 'analytics_events'), eventData)
            console.log('✅ Event stored with ID:', docRef.id)
          } catch (error) {
            console.warn('⚠️ Standard Firestore write failed:', error)
            // Store locally as fallback for all events
            this.storeEventLocally(eventType, eventData)
          }
        }
        
        createAuditLog('analytics_event_stored', { eventType, sessionId: this.sessionId })
        
        // Store in detailed logs only for key business metrics
        if (['session_start', 'completion_reached', 'survey_completed'].includes(eventType)) {
          await this.saveDetailedLogForEvent(eventType, deviceInfo.deviceType, eventData)
        }
      } else {
        console.log('⚠️ No database connection - storing event locally:', eventType)
        console.log('⚠️ Database connection status:', { db: !!db, firebaseInitialized: !!db })

        // Enhanced fallback: Store in localStorage with additional metadata
        const enhancedEventData = {
          ...eventData,
          connectionStatus: 'no_db',
          timestamp: new Date().toISOString(),
          userAgent: navigator.userAgent.substring(0, 100),
          hostname: window.location.hostname
        }

        this.storeEventLocally(eventType, enhancedEventData)

        // Also store critical events in a special backup location
        const isCriticalEvent = ['session_start', 'audio_milestone_3min', 'completion_reached', 'survey_completed'].includes(eventType)
        if (isCriticalEvent) {
          const criticalBackupKey = `hep5_critical_backup_${eventType}_${this.sessionId}_${Date.now()}`
          localStorage.setItem(criticalBackupKey, JSON.stringify({
            eventType,
            eventData: enhancedEventData,
            reason: 'no_database_connection',
            timestamp: new Date().toISOString()
          }))
          console.log('💾 Critical event stored in backup location:', criticalBackupKey)

          // iPhone specific: Additional backup with unique key per critical event
          const isIPhone = /iPhone/.test(navigator.userAgent)
          if (isIPhone) {
            const iphoneBackupKey = `hep5_iphone_${eventType}_${this.sessionId}`
            const iphoneBackupData = {
              eventType,
              sessionId: this.sessionId,
              timestamp: new Date().toISOString(),
              additionalData,
              reason: 'iphone_no_db_connection',
              url: window.location.href,
              userAgent: navigator.userAgent
            }
            localStorage.setItem(iphoneBackupKey, JSON.stringify(iphoneBackupData))
            console.log('📱🍎 iPhone specific backup stored:', iphoneBackupKey)

            // Also store in a consolidated iPhone events array
            const existingIPhoneEvents = JSON.parse(localStorage.getItem('hep5_iphone_events') || '[]')
            existingIPhoneEvents.push(iphoneBackupData)
            localStorage.setItem('hep5_iphone_events', JSON.stringify(existingIPhoneEvents))
            console.log('📱🍎 Added to iPhone events array, total:', existingIPhoneEvents.length)
          }
        }
      }
      
      // Also track session summary for quick access
      if (eventType === 'session_start') {
        await this.initializeSessionSummary()
      } else if (eventType === 'session_end') {
        await this.updateSessionSummary()
      }
      
      // Reduce console spam - only log important events
      if (eventType === 'error_occurred' || eventType === 'session_start' || eventType === 'completion_reached' || eventType === 'survey_completed') {
        console.log('Analytics event tracked:', eventType, eventType === 'survey_completed' ? { rating: eventData.surveyRating } : '')
      }
      
    } catch (error) {
      console.error('Failed to track analytics event:', error)
      createAuditLog('analytics_error', { eventType, error: error instanceof Error ? error.message : 'Unknown error' })
      // Don't throw error to avoid disrupting user experience
    }
  }
  
  private async initializeSessionSummary() {
    try {
      if (!db) {
        console.log('Session summary (demo mode): initialized for session', this.sessionId)
        return
      }
      
      const deviceInfo = this.getDeviceInfo()
      
      const sessionSummary = {
        sessionId: this.sessionId,
        startTime: serverTimestamp(),
        endTime: null,
        duration: 0,
        deviceType: deviceInfo.deviceType,
        browserInfo: deviceInfo.browserInfo,
        completed: false,
        surveyCompleted: false,
        surveyRating: null,
        audioPlayDuration: 0,
        errorsOccurred: 0,
        lastActivity: serverTimestamp()
      }
      
      await addDoc(collection(db, 'session_summaries'), sessionSummary)
      
    } catch (error) {
      console.error('Failed to initialize session summary:', error)
    }
  }
  
  private async updateSessionSummary() {
    try {
      // Update session summary with end time and duration
      const sessionDuration = Date.now() - this.sessionStartTime
      
      // Note: In a full implementation, we'd update the existing document
      // For now, we'll create an end event with summary data
      await this.trackEvent('session_end', {
        playbackDuration: sessionDuration / 1000
      })
      
    } catch (error) {
      console.error('Failed to update session summary:', error)
    }
  }
  
  // Convenience methods for common events
  public async trackSessionStart() {
    await this.trackEvent('session_start')
  }
  
  public async trackSessionEnd() {
    await this.trackEvent('session_end')
  }
  
  public async trackAudioPlayStart(ttfp?: number) {
    await this.trackEvent('audio_play_start', { ttfp })
  }
  
  public async trackAudioPlayPause(position: number, duration: number) {
    await this.trackEvent('audio_play_pause', {
      audioPosition: position,
      playbackDuration: duration
    })
  }
  
  public async trackAudio5MinMilestone(position: number) {
    await this.trackEvent('audio_milestone_5min', {
      audioPosition: position,
      playbackDuration: 300 // 5 minutes
    })
  }

  public async trackAudio3MinMilestone(position: number) {
    const eventKey = `audio_milestone_3min-${this.sessionId}`;
    
    // Prevent duplicate 3-minute milestones for the same session
    if (this.loggedEvents.has(eventKey)) {
      console.log('3-minute milestone already tracked for this session');
      return;
    }
    
    await this.trackEvent('audio_milestone_3min', {
      audioPosition: position,
      playbackDuration: 180 // 3 minutes
    })
    
    // Mark as logged to prevent duplicates
    this.loggedEvents.add(eventKey);
    this.saveLoggedEvents();
  }
  
  public async trackSurveyDisplayed() {
    await this.trackEvent('survey_displayed')
  }
  
  public async trackSurveyCompleted(rating: 'excellent' | 'good' | 'fair' | 'poor') {
    console.log('🔵 [ANALYTICS DEBUG] trackSurveyCompleted called with rating:', rating, 'sessionId:', this.sessionId)
    
    // For iPhone Safari, add extra robustness
    const isIPhoneSafari = /iPhone.*Safari/.test(navigator.userAgent)
    if (isIPhoneSafari) {
      console.log('🍎 [SAFARI DEBUG] iPhone Safari detected, using ultra-robust tracking approach')
      
      // Immediate localStorage backup BEFORE attempting Firestore
      const immediateBackup: {
        type: string;
        rating: 'excellent' | 'good' | 'fair' | 'poor';
        sessionId: string;
        timestamp: string;
        attempted: number;
        status: string;
        error?: string;
      } = {
        type: 'survey_completed',
        rating,
        sessionId: this.sessionId,
        timestamp: new Date().toISOString(),
        attempted: Date.now(),
        status: 'attempting'
      };
      localStorage.setItem('hep5_immediate_survey_backup', JSON.stringify(immediateBackup));
      console.log('🍎 [SAFARI DEBUG] Immediate backup stored before Firestore attempt');
      
      try {
        // Use much shorter timeout to prevent blocking
        const trackingPromise = this.trackEvent('survey_completed', { surveyRating: rating })
        const timeoutPromise = new Promise<never>((_, reject) => {
          setTimeout(() => reject(new Error('Tracking timeout')), 2000) // Reduced to 2 seconds
        })
        
        await Promise.race([trackingPromise, timeoutPromise])
        
        // Update backup status to success
        immediateBackup.status = 'success';
        localStorage.setItem('hep5_immediate_survey_backup', JSON.stringify(immediateBackup));
        console.log('🍎 [SAFARI DEBUG] Survey tracking completed successfully, backup updated')
        
      } catch (error) {
        console.error('🍎 [SAFARI DEBUG] Survey tracking failed, keeping backup:', error)
        
        // Update backup status to failed but keep the data
        immediateBackup.status = 'failed';
        immediateBackup.error = error instanceof Error ? error.message : String(error);
        localStorage.setItem('hep5_immediate_survey_backup', JSON.stringify(immediateBackup));
        
        // Additional fallback: try to force a synchronous-like write
        console.log('🍎 [SAFARI DEBUG] Attempting emergency synchronous-style backup');
        try {
          // This won't actually be synchronous, but we'll try without await to not block
          this.trackEvent('survey_completed', { surveyRating: rating }).catch(() => {
            console.log('🍎 [SAFARI DEBUG] Emergency backup also failed, but continuing');
          });
        } catch (emergencyError) {
          console.log('🍎 [SAFARI DEBUG] Emergency backup failed');
        }
        
        // Don't throw - we have backups
        console.log('🍎 [SAFARI DEBUG] Continuing despite Firestore failure - we have localStorage backup');
      }
    } else {
      await this.trackEvent('survey_completed', { surveyRating: rating })
    }
    
    console.log('🔵 [ANALYTICS DEBUG] trackSurveyCompleted completed')
  }
  
  public async trackSurveySkipped() {
    await this.trackEvent('survey_skipped')
  }
  
  public async trackCompletionReached(totalDuration: number) {
    console.log('🎯 [COMPLETION DEBUG] trackCompletionReached called with duration:', totalDuration);
    
    const eventKey = `completion_reached-${this.sessionId}`;
    
    // Prevent duplicate completion events for the same session
    if (this.loggedEvents.has(eventKey)) {
      console.log('🔒 [DUPLICATE PREVENTION] Completion already tracked for this session');
      return;
    }
    
    await this.trackEvent('completion_reached', {
      playbackDuration: totalDuration
    })
    
    // Mark as logged to prevent duplicates
    this.loggedEvents.add(eventKey);
    this.saveLoggedEvents();
    
    console.log('🎯 [COMPLETION DEBUG] trackCompletionReached completed');
  }
  
  public async trackError(
    errorType: 'network' | 'device' | 'hls' | 'session' | 'timeout' | 'general',
    errorMessage: string
  ) {
    await this.trackEvent('error_occurred', {
      errorType,
      errorMessage: errorMessage.substring(0, 500) // Limit error message length
    })
  }

  public async trackPerformanceMetric(
    metricName: string, 
    value: number, 
    rating: 'good' | 'needs-improvement' | 'poor'
  ) {
    await this.trackEvent('performance_metric', {
      performanceMetricName: metricName,
      performanceMetricValue: value,
      performanceRating: rating
    })
  }
  
  private getEmptyStats() {
    return {
      visitorsCount: 0,
      playersCount: 0,
      play3MinCount: 0,
      completersCount: 0,
      surveyRespondentsCount: 0,
      totalEvents: 0,
      uniqueSessions: 0,
      completionRate: 0,
      surveyResponseRate: 0,
      errorRate: 0,
      events: []
    }
  }
  
  // Analytics data retrieval (for dashboard)
  public async getSessionStats(hours = 24) {
    try {
      // Quick check for database connection
      if (!db) {
        console.log('📊 No database connection - returning empty stats immediately')
        return this.getEmptyStats()
      }
      
      console.log('🔍 Database connected, starting query...')
      const queryStartTime = Date.now()
      
      // First, try a simple count query to test basic connectivity
      console.log('🔍 Testing basic connectivity with simple query...')
      const testQuery = query(collection(db, 'analytics_events'), limit(1))
      const testSnapshot = await getDocs(testQuery)
      console.log(`✅ Test query successful, found ${testSnapshot.size} docs`)
      
      // Set a longer timeout for the query
      const timeoutPromise = new Promise<never>((_, reject) => {
        setTimeout(() => reject(new Error('Query timeout after 10 seconds')), 10000)
      })
      
      let q
      if (hours === -1) {
        // All time - simple query without ordering to avoid index issues
        q = query(
          collection(db, 'analytics_events'),
          limit(1000) // Limit to last 1000 events for performance
        )
      } else {
        // For now, get all events and filter in memory to avoid timestamp ordering issues
        q = query(
          collection(db, 'analytics_events'),
          limit(500) // Limit for performance
        )
      }
      
      console.log('📋 Query created, executing...')
      const snapshot = await Promise.race([getDocs(q), timeoutPromise])
      
      const queryEndTime = Date.now()
      console.log(`⚡ Query completed in ${queryEndTime - queryStartTime}ms, docs: ${snapshot.docs?.length || 0}`)
      
      let events = snapshot.docs.map(doc => {
        const docData = doc.data()
        const event = {
          id: doc.id,
          ...docData
        } as AnalyticsEventData & { id: string }
        return event
      })
      
      console.log(`📊 Raw events loaded: ${events.length}`)
      
      // Apply time filtering in memory if needed
      if (hours !== -1) {
        const cutoffTime = Date.now() - (hours * 60 * 60 * 1000)
        events = events.filter(event => {
          if (event.timestamp && typeof event.timestamp === 'object' && event.timestamp.seconds) {
            // Firebase Timestamp object
            const eventTime = event.timestamp.seconds * 1000
            return eventTime >= cutoffTime
          } else if (event.timestamp instanceof Date) {
            // JavaScript Date object
            return event.timestamp.getTime() >= cutoffTime
          } else if (typeof event.timestamp === 'number') {
            // Unix timestamp
            return event.timestamp >= cutoffTime
          }
          // If timestamp format is unknown, include the event
          return true
        })
        console.log(`📊 Events after time filtering (${hours}h): ${events.length}`)
      }
      
      // Calculate specific metrics requested
      const sessionStartEvents = events.filter(e => e.eventType === 'session_start')
      const audioPlayStartEvents = events.filter(e => e.eventType === 'audio_play_start')
      const audio3MinEvents = events.filter(e => e.eventType === 'audio_milestone_3min')
      const completionEvents = events.filter(e => e.eventType === 'completion_reached')
      const surveyEvents = events.filter(e => e.eventType === 'survey_completed')
      
      console.log('📊 Event type counts:', {
        session_start: sessionStartEvents.length,
        audio_play_start: audioPlayStartEvents.length,
        audio_milestone_3min: audio3MinEvents.length,
        completion_reached: completionEvents.length,
        survey_completed: surveyEvents.length,
        total: events.length
      })
      
      // Event counts calculated - logging removed for performance
      
      // Survey events debugging removed for performance
      
      // Get unique session counts for each metric
      const visitorsCount = new Set(sessionStartEvents.map(e => e.sessionId)).size
      const playersCount = new Set(audioPlayStartEvents.map(e => e.sessionId)).size
      const play3MinCount = new Set(audio3MinEvents.map(e => e.sessionId)).size
      const completersCount = new Set(completionEvents.map(e => e.sessionId)).size
      const surveyRespondentsCount = new Set(surveyEvents.map(e => e.sessionId)).size
      
      // Unique session counts calculated - logging removed for performance
      
      const stats = {
        // New metrics
        visitorsCount,
        playersCount, 
        play3MinCount,
        completersCount,
        surveyRespondentsCount,
        
        // Legacy metrics for compatibility
        totalEvents: events.length,
        uniqueSessions: visitorsCount,
        completionRate: visitorsCount > 0 ? (completersCount / visitorsCount) * 100 : 0,
        surveyResponseRate: visitorsCount > 0 ? (surveyRespondentsCount / visitorsCount) * 100 : 0,
        errorRate: 0, // Simplified for new dashboard
        events: events // Return all events for proper survey breakdown calculation
      }
      
      return stats
      
    } catch (error) {
      console.error('❌ Failed to get session stats:', error)
      console.error('Error details:', {
        message: error instanceof Error ? error.message : 'Unknown error',
        stack: error instanceof Error ? error.stack : undefined
      })
      // Return empty stats instead of throwing to prevent hanging
      return this.getEmptyStats()
    }
  }
  
  public getSessionId(): string {
    return this.sessionId
  }

  // Debug function to check iPhone localStorage data
  public getIPhoneStoredEvents() {
    const iphoneEvents = JSON.parse(localStorage.getItem('hep5_iphone_events') || '[]')
    const allStorageKeys = Object.keys(localStorage).filter(key => key.startsWith('hep5_'))

    console.log('📱🍎 [IPHONE DEBUG] iPhone events in storage:', iphoneEvents.length)
    console.log('📱🍎 [IPHONE DEBUG] All hep5 localStorage keys:', allStorageKeys.length)

    return {
      iphoneEvents,
      allKeys: allStorageKeys,
      totalStoredItems: allStorageKeys.length
    }
  }

  // New detailed log system with enhanced duplicate prevention
  public async logDetailedEvent(eventType: 'Visit' | 'Play' | 'Play3min' | 'Completion' | 'Survey', deviceType: 'mobile' | 'pc', additionalInfo?: string) {
    const eventKey = `${eventType}-${this.sessionId}`
    
    // Enhanced duplicate check: localStorage + in-memory
    const localStorageKey = `hep5_detailed_${eventKey}`
    const alreadyLogged = this.loggedEvents.has(eventKey) || localStorage.getItem(localStorageKey) === 'true'
    
    if (alreadyLogged) {
      console.log(`🔒 [DUPLICATE PREVENTION] Event ${eventType} already logged for session ${this.sessionId}`)
      return
    }
    
    // Mark as logged IMMEDIATELY to prevent race conditions
    this.loggedEvents.add(eventKey)
    localStorage.setItem(localStorageKey, 'true')
    this.saveLoggedEvents()
    console.log(`🔐 [DUPLICATE PREVENTION] Marked ${eventType} as logged for session ${this.sessionId}`)

    try {
      if (db) {
        const logEntry = {
          sessionId: this.sessionId,
          eventType,
          deviceType,
          additionalInfo: additionalInfo || '',
          timestamp: serverTimestamp()
        }

        const isIPhoneSafari = /iPhone.*Safari/.test(navigator.userAgent)
        
        if (isIPhoneSafari && eventType === 'Survey') {
          console.log('🍎 [SAFARI DEBUG] Using retry mechanism for detailed Survey log')
          
          // Retry mechanism for iPhone Safari survey detailed logs
          let attempts = 0
          const maxAttempts = 2
          
          while (attempts < maxAttempts) {
            try {
              attempts++
              console.log(`🍎 [SAFARI DEBUG] Detailed log attempt ${attempts}/${maxAttempts}`)
              
              await Promise.race([
                addDoc(collection(db, 'detailed_logs'), logEntry),
                new Promise<never>((_, reject) => {
                  setTimeout(() => reject(new Error(`Detailed log timeout on attempt ${attempts}`)), 2000)
                })
              ])
              
              console.log(`🍎 [SAFARI DEBUG] Detailed Survey log saved successfully on attempt ${attempts}`)
              break
              
            } catch (error) {
              console.error(`🍎 [SAFARI DEBUG] Detailed log attempt ${attempts} failed:`, error)
              
              if (attempts >= maxAttempts) {
                console.error('🍎 [SAFARI DEBUG] All detailed log attempts failed')
                // Don't throw - we've already marked as logged
                break
              }
              
              // Shorter wait for detailed logs
              await new Promise(resolve => setTimeout(resolve, 500))
            }
          }
        } else {
          // Normal single attempt with error handling
          try {
            await addDoc(collection(db, 'detailed_logs'), logEntry)
            console.log(`✅ Detailed log saved: ${eventType} for session ${this.sessionId}`)
          } catch (error) {
            console.error(`❌ Failed to save detailed log for ${eventType}:`, error)
            // Don't throw - we've already marked as logged to prevent retries
          }
        }
      }
    } catch (error) {
      console.error('Critical error in detailed log system:', error)
      // Already marked as logged, so no retry will occur
    }
  }

  // Internal method to save detailed logs from trackEvent 
  private async saveDetailedLogForEvent(eventType: AnalyticsEvent, deviceType: 'desktop' | 'tablet' | 'mobile', eventData: AnalyticsEventData) {
    console.log('🟡 [DETAILED LOG DEBUG] saveDetailedLogForEvent called:', eventType, 'deviceType:', deviceType)
    
    let detailedEventType: 'Visit' | 'Play' | 'Play3min' | 'Completion' | 'Survey' | null = null
    let additionalInfo = ''
    
    // Map analytics events to detailed log events
    switch (eventType) {
      case 'session_start':
        detailedEventType = 'Visit'
        break
      case 'audio_play_start':
        detailedEventType = 'Play'
        break
      case 'audio_milestone_3min':
        detailedEventType = 'Play3min'
        break
      case 'completion_reached':
        detailedEventType = 'Completion'
        break
      case 'survey_completed':
        detailedEventType = 'Survey'
        additionalInfo = eventData.surveyRating || ''
        console.log('🟡 [DETAILED LOG DEBUG] Survey event mapped, rating:', additionalInfo)
        break
      default:
        // Don't create detailed logs for other events
        console.log('🟡 [DETAILED LOG DEBUG] Event type not mapped for detailed logs:', eventType)
        return
    }

    // Convert device type to simplified format
    const simplifiedDeviceType: 'mobile' | 'pc' = (deviceType === 'mobile' || deviceType === 'tablet') ? 'mobile' : 'pc'
    
    console.log('🟡 [DETAILED LOG DEBUG] About to call logDetailedEvent:', detailedEventType, simplifiedDeviceType, additionalInfo)
    
    // Save detailed log (with duplicate prevention)
    await this.logDetailedEvent(detailedEventType, simplifiedDeviceType, additionalInfo)
    
    console.log('🟡 [DETAILED LOG DEBUG] logDetailedEvent completed')
  }

  // Initialize Visit event when user first arrives
  public async initializeVisit() {
    const deviceType = this.getDeviceInfo().deviceType === 'mobile' || this.getDeviceInfo().deviceType === 'tablet' ? 'mobile' : 'pc'
    await this.logDetailedEvent('Visit', deviceType)
  }

  // Store event locally when Firebase is not available
  private storeEventLocally(eventType: AnalyticsEvent, eventData: AnalyticsEventData) {
    try {
      const storageKey = `hep5_offline_${eventType}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
      const localData = {
        eventType,
        eventData: {
          ...eventData,
          timestamp: new Date().toISOString() // Convert to string for localStorage
        },
        storedAt: new Date().toISOString(),
        sessionId: this.sessionId
      }
      
      localStorage.setItem(storageKey, JSON.stringify(localData))
      console.log(`📱 Event stored locally: ${eventType}`, storageKey)
      
      // Keep only recent events in localStorage (last 100 events)
      this.cleanupLocalStorage()
      
    } catch (error) {
      console.error('Failed to store event locally:', error)
    }
  }

  // Clean up old local storage entries to prevent storage overflow
  private cleanupLocalStorage() {
    try {
      const keys = Object.keys(localStorage).filter(key => key.startsWith('hep5_offline_'))
      if (keys.length > 100) {
        // Sort by timestamp and remove oldest entries
        keys.sort().slice(0, keys.length - 100).forEach(key => {
          localStorage.removeItem(key)
        })
      }
    } catch (error) {
      console.error('Failed to cleanup localStorage:', error)
    }
  }
}

// Export singleton instance
export const analytics = AnalyticsService.getInstance()

// Auto-track page visibility changes
document.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'hidden') {
    // User left the page/tab
    analytics.trackSessionEnd()
  }
})

// Note: Session start is now only tracked from LandingPage.tsx